/**
 * The new layout (beta): one shell for every screen (top bar, retractable sidebar, Lykke's panel, a rounded
 * workspace, a right drawer). Behind a per person setting: off in production, on for Beatly staff on staging,
 * and anyone can switch it in Settings (staff also from the profile menu).
 *
 * Also the shell's own preferences per person: sidebar Pin or Float (like the back office, BO #357), Lykke's panel
 * open or closed and its width, and the chrome's theme. Stored in localStorage under the person's e-mail, so two
 * people on one browser keep their own; a blocked storage (private window) falls back to the defaults.
 *
 * Pure TypeScript: no React.
 */

export type SidebarMode = 'pinned' | 'float';
export type ShellTheme = 'light' | 'dark' | 'system';
export interface ShellPrefs { sidebar: SidebarMode; agentOpen: boolean; agentWidth: number; theme: ShellTheme }

export const AGENT_MIN_W = 320;
export const AGENT_MAX_W = 560;
export const AGENT_DEFAULT_W = 380;
export const DEFAULT_PREFS: ShellPrefs = { sidebar: 'float', agentOpen: true, agentWidth: AGENT_DEFAULT_W, theme: 'light' };

/** The event the shell listens to when the setting changes in another part of the app (Settings, the profile menu). */
export const LAYOUT_EVENT = 'omni-layout-change';

const who = (email?: string | null) => (email || 'anyone').trim().toLowerCase();
const layoutKey = (email?: string | null) => `omni.layout.v2:${who(email)}`;
const prefsKey = (email?: string | null) => `omni.shell.prefs:${who(email)}`;

function read(key: string): string | null {
  try { return typeof localStorage === 'undefined' ? null : localStorage.getItem(key); } catch { return null; }
}
function write(key: string, value: string | null): void {
  try {
    if (typeof localStorage === 'undefined') return;
    if (value === null) localStorage.removeItem(key); else localStorage.setItem(key, value);
  } catch { /* storage off: the choice lasts this visit only */ }
}

export interface LayoutViewer { email?: string | null; staff: boolean; environment?: string | null }

/** What the person sees when they never chose: on for staff on staging, off for everyone else (production included). */
export function defaultNewLayout(v: LayoutViewer): boolean {
  return v.staff && (v.environment || '').toLowerCase() === 'staging';
}

/** The person's choice, or the default when they never chose. `?layout=new|classic` wins for this visit (tests, links). */
export function newLayoutOn(v: LayoutViewer, search?: string): boolean {
  const q = typeof search === 'string' ? new URLSearchParams(search).get('layout') : null;
  if (q === 'new') return true;
  if (q === 'classic') return false;
  const stored = read(layoutKey(v.email));
  if (stored === 'on') return true;
  if (stored === 'off') return false;
  return defaultNewLayout(v);
}

/** Saves the choice and tells the shell (same tab) to switch now. */
export function setNewLayout(email: string | null | undefined, on: boolean): void {
  write(layoutKey(email), on ? 'on' : 'off');
  try { window.dispatchEvent(new CustomEvent(LAYOUT_EVENT, { detail: { on } })); } catch { /* no window (tests) */ }
}

/** Whether this person may switch the setting here: staff anywhere, everyone on staging and in local dev. */
export function maySwitchLayout(v: LayoutViewer): boolean {
  const env = (v.environment || '').toLowerCase();
  return v.staff || env === 'staging' || env === 'development' || env === '';
}

const clampW = (n: unknown) => (typeof n === 'number' && Number.isFinite(n) ? Math.min(AGENT_MAX_W, Math.max(AGENT_MIN_W, Math.round(n))) : AGENT_DEFAULT_W);

export function readShellPrefs(email?: string | null): ShellPrefs {
  const raw = read(prefsKey(email));
  if (!raw) return { ...DEFAULT_PREFS };
  try {
    const p = JSON.parse(raw) as Partial<ShellPrefs>;
    return {
      sidebar: p.sidebar === 'pinned' || p.sidebar === 'float' ? p.sidebar : DEFAULT_PREFS.sidebar,
      agentOpen: typeof p.agentOpen === 'boolean' ? p.agentOpen : DEFAULT_PREFS.agentOpen,
      agentWidth: clampW(p.agentWidth),
      theme: p.theme === 'dark' || p.theme === 'system' || p.theme === 'light' ? p.theme : DEFAULT_PREFS.theme,
    };
  } catch { return { ...DEFAULT_PREFS }; }
}

export function writeShellPrefs(email: string | null | undefined, prefs: ShellPrefs): void {
  write(prefsKey(email), JSON.stringify({ ...prefs, agentWidth: clampW(prefs.agentWidth) }));
}

export { clampW as clampAgentWidth };
